import emptyStateImage from "$assets/images/empty-state.svg?url";
import { Button } from "$components/button/button";
import { PlusIconBorderless } from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { useCreateInvoiceRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-create-invoice-rxdb";
import { linkToPatientDetails } from "$routes/patient/[:patientId]/links";
import { Box, Typography } from "@material-ui/core";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import useCreateInvoice from "./hooks/useCreateInvoice";

const useStyles = makeStyles((theme) => ({
  emptyStateImage: {
    margin: "20px auto",
    width: "100px",
  },
  title: {
    fontWeight: 500,
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

const EmptyStateLayout = ({ onCreate }: { onCreate: () => void }) => {
  const classes = useStyles();
  return (
    <Box textAlign="center" py={5}>
      <img className={classes.emptyStateImage} src={emptyStateImage} alt="" />
      <Typography
        className={classes.title}
        variant="body1"
        color="textSecondary"
      >
        <FormattedMessage
          id="invoicing.emptyState.title"
          defaultMessage="No invoices yet"
        />
      </Typography>
      <Typography gutterBottom variant="body2" color="textSecondary">
        <FormattedMessage
          id="invoicing.emptyState.description"
          defaultMessage="Invoices created for this patient will appear here"
        />
      </Typography>
      <Button
        className={classes.button}
        startIcon={<PlusIconBorderless />}
        variant="contained"
        color="secondary"
        onClick={onCreate}
      >
        <FormattedMessage
          id="invoicing.emptyState.buttonLabel"
          defaultMessage="Create invoice"
        />
      </Button>
    </Box>
  );
};

const OnlineEmptyState = ({ patientUuid }: { patientUuid: string }) => {
  const { createInvoice } = useCreateInvoice(patientUuid);
  const onCreate = useCallback(() => {
    createInvoice().catch(() => {
      // useCreateInvoice surfaces errors via toast
    });
  }, [createInvoice]);
  return <EmptyStateLayout onCreate={onCreate} />;
};

const RxdbEmptyState = ({ patientUuid }: { patientUuid: string }) => {
  const { createInvoice } = useCreateInvoiceRxdb(patientUuid);
  const navigate = useNavigate();
  const onCreate = useCallback(async () => {
    const result = await createInvoice();
    if (!result) return;
    navigate(linkToPatientDetails({ patientUuid, invoiceId: result.id }));
  }, [createInvoice, navigate, patientUuid]);
  return <EmptyStateLayout onCreate={onCreate} />;
};

const InvoicingEmptyState = () => {
  const { patientUuid } = useQuerystring();
  const [rxdbBillingFlag, { loading: flagLoading }] = useFeatureFlag<boolean>(
    featureFlags.billing.rxdbBilling,
  );
  if (flagLoading) return null;
  return rxdbBillingFlag ? (
    <RxdbEmptyState patientUuid={patientUuid} />
  ) : (
    <OnlineEmptyState patientUuid={patientUuid} />
  );
};

export default InvoicingEmptyState;
